import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Bar, Line } from 'react-chartjs-2';
import { prepareBarChartData, prepareLineChartData, prepareOptions } from '../../../utils/chart-utils';
import makeStyles from '@material-ui/core/styles/makeStyles';
import './RoundedBarCharts';
import { useTheme } from '@material-ui/core';
import { ThemeContext } from '../../../config/theme';
import {
  BACKGROUND_DARK, BORDER_DARK, VERY_LIGHT_GREY_COLOR,
} from '../../../const/color-const';
import MoneyFormat from '../MoneyFormat';
import { NUMBER_OF_SOLD_GOODS, RETURNS_VAT, SALES_VAT } from './chartDataConfig';
import { darken } from '@material-ui/core/styles';

const DEFAULT_CURRENCY = 'SEK';

const useStyles = makeStyles(theme => ({
  chartArea: props => ({
    width: '100%',
    boxSizing: 'border-box',
    margin: theme.spacing(0),
    paddingTop: theme.spacing(1),
    height: props.dashboardChart ? 285 : 360,
    backgroundColor: theme.palette.type === 'light' ? 'white' : darken(BACKGROUND_DARK, 0.2),
    borderTop: theme.palette.type === 'light' ? `1px solid ${VERY_LIGHT_GREY_COLOR}` : `1px solid ${BORDER_DARK}`,
    '& canvas': {
      padding: theme.spacing(0, 1, 0, 0.5),
    },
  }),
  totalSalesWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    height: 70,
  },
  totalValues: {
    textAlign: 'center',
    fontSize: 14,
    margin: 0,
    padding: 0,
  },
}));

const totalTranslation = () => <FormattedMessage id="report.title.Total" defaultMessage="Total" />;
const includingVAT = () => <FormattedMessage id="report.title.IncludingVat" defaultMessage="(incl. VAT)" />;
const excludingVAT = () => <FormattedMessage id="report.title.ExcludingVat" defaultMessage="(exkl. VAT)" />;

const generateData = (dataValues, classes) => {
  return (
    <div className={classes.totalSalesWrapper}>
      {dataValues.map((item, index) => {
        return (
          <div key={index}>
            {generateTotalContent(item, classes)}
          </div>
        );
      })}
    </div>
  );
};

const generateTotalContent = ({ id, totalValue }, classes) => {
  // eslint-disable-next-line default-case
  switch (id) {
    case NUMBER_OF_SOLD_GOODS.legends[0].id:
      return (totalValue !== 0 &&
        <div className={classes.totalValues}>
          <strong>{totalTranslation()}:</strong>&nbsp;
          {parseInt(totalValue)} <FormattedMessage id="report.chart.items" defaultMessage="items" />
        </div>
      );
    case SALES_VAT.legends[0].id:
    case RETURNS_VAT.legends[0].id:
      return (totalValue !== 0 &&
        <div className={classes.totalValues}>
          <strong>{totalTranslation()}:</strong>&nbsp;
          <MoneyFormat value={totalValue.toString()} /> {DEFAULT_CURRENCY}&nbsp;
          <strong>{includingVAT()}</strong>
        </div>
      );
    case SALES_VAT.legends[1].id:
    case RETURNS_VAT.legends[1].id:
      return (totalValue !== 0 &&
        <div className={classes.totalValues}>
          <strong>{totalTranslation()}:</strong>&nbsp;
          <MoneyFormat value={totalValue.toString()} /> {DEFAULT_CURRENCY}&nbsp;
          <strong>{excludingVAT()}</strong>
        </div>
      );
  }
};

const Chart = (props) => {
  const {
    chartData,
    totalValues,
    dashboardChart,
    chartData: {
      title,
      legends,
      time,
    },
  } = props;
  
  const classes = useStyles({ dashboardChart: dashboardChart });
  const { formatMessage } = useIntl();
  const translatedTitle = formatMessage({ id: `report.chart.${title.id}`, defaultMessage: title.defaultMessage });
  const translatedLegends = legends.map(legend => formatMessage({
    id: `report.chart.${legend.id}`,
    defaultMessage: legend.defaultMessage,
  }));
  
  const data = {
    ...chartData,
    title: translatedTitle,
    legends: translatedLegends,
  };
  
  const plugins = [{
    beforeInit: function (chart) {
      chart.legend.afterFit = function () {
        this.height = this.height + 16;
      };
    },
  }];
  
  const theme = useTheme();
  
  return (
    <ThemeContext.Consumer>
      {({ darkState }) =>
        <div className={classes.chartArea}>
          {!!time ? (<Line
            data={prepareLineChartData(data)}
            options={prepareOptions(data, theme, darkState)}
            plugins={plugins}
          />) : (<Bar
            data={prepareBarChartData(data)}
            options={prepareOptions(data, theme, darkState)}
            plugins={plugins}
          />)}
          {totalValues && generateData(totalValues, classes)}
        </div>
      }
    </ThemeContext.Consumer>
  );
};

Chart.propTypes = {
  chartData: PropTypes.object.isRequired,
};

export default Chart;
